/**
 * 引用行を除外してテキストを取得（&gt; または > で始まる行を除外）
 * @param text - プレーンテキスト
 * @returns 引用行を除いたテキスト
 */
export function stripQuoteLines(text: string): string {
  return text
    .split("\n")
    .filter((line) => {
      const trimmed = line.trim();
      return !trimmed.startsWith("&gt;") && !trimmed.startsWith(">");
    })
    .join("\n");
}

/**
 * 引用ブロックの情報。
 * textは引用記号を除いた検索用の本文、sourceは表示中の引用記法を保持する。
 */
export interface QuoteTextBlock {
  text: string;
  source: string;
  lineIndexes: number[];
}

function parseQuoteLine(line: string): string | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith("&gt;") && !trimmed.startsWith(">")) return null;
  const quoteText = trimmed.replace(/^(?:(?:&gt;|>)+)\s*/, "").trim();
  return quoteText || null;
}

/**
 * 引用記法を解析する。複数行の場合は各行の引用記号を除去して結合する。
 * 引用でない行や内容が空の引用を含む場合はnullを返す。
 */
export function parseQuoteText(text: string): string | null {
  const quoteTexts = text.split(/\r?\n/).map(parseQuoteLine);
  if (quoteTexts.some((quoteText) => quoteText === null)) return null;
  return quoteTexts.join("\n") || null;
}

/**
 * 引用検索へ渡す本文を引用記号なしに正規化する。
 */
export function getQuoteSearchText(quoteText: string): string {
  return parseQuoteText(quoteText) ?? quoteText.replace(/^>/g, "").trim();
}

/**
 * 本文から連続する引用行をブロック単位で抽出する。
 */
export function extractQuoteTextBlocks(text: string): QuoteTextBlock[] {
  const blocks: QuoteTextBlock[] = [];
  let currentText: string[] = [];
  let currentSource: string[] = [];
  let currentLineIndexes: number[] = [];

  const flush = (): void => {
    if (currentText.length === 0) return;
    blocks.push({
      text: currentText.join("\n"),
      source: currentSource.join("\n"),
      lineIndexes: currentLineIndexes,
    });
    currentText = [];
    currentSource = [];
    currentLineIndexes = [];
  };

  text.split(/\r?\n/).forEach((line, lineIndex) => {
    const quoteText = parseQuoteLine(line);
    if (quoteText === null) {
      flush();
      return;
    }
    // 空白の許容範囲は resolveQuotedPost 側と揃える。片側だけ緩めると
    // 「> No. 123」が独立ブロックにならず、レス番号への解決先を失う。
    if (/^No\.\s*\d+$/i.test(quoteText)) {
      flush();
      blocks.push({
        text: quoteText,
        source: line,
        lineIndexes: [lineIndex],
      });
      return;
    }
    currentText.push(quoteText);
    currentSource.push(line);
    currentLineIndexes.push(lineIndex);
  });
  flush();

  return blocks;
}

/**
 * 本文から引用本文をブロック単位で抽出する。
 */
export function extractQuoteTexts(text: string): string[] {
  return extractQuoteTextBlocks(text).map((block) => block.text);
}
